/* eslint react/destructuring-assignment: 0 */
import classnames from 'classnames'
import React from 'react'
import { CheckMarks } from '../../elements/CheckMarks'
import { HeroHeadings } from '../../elements/HeroHeadings'
import { ColorName as themeNames, containerMaxWidth, mq, mqBreakpoints, Color } from '../../utils'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import { IAuxContentLayout, IProps } from './index'
import { Image } from 'marketing-site/src/library/elements/Image'
import styles from './styles.scss'

export function Hero({
  eyebrow,
  headline,
  headline2,
  subheading,
  layout,
  largeFont,
  renderMainContent,
  media,
  bgColor = Color.White,
  bottomTexture = false,
  leftAlignMedia = false,
  avatars = [],
  disableGridForWrapper = false,
  disableGridForBody = false,
  checkmarks,
}: IProps): React.ReactElement {
  function renderMainContentDefault() {
    return checkmarks && <CheckMarks {...checkmarks} />
  }

  const hasAuxContent = media && Object.keys(media).includes('renderAuxContent')

  const withLeftAlignedMedia =
    hasAuxContent && !(media as IAuxContentLayout).isBackground && leftAlignMedia

  const spacing = useSpacing()

  const heroWrapper = classnames({
    'hero-wrapper': true,
    'one-column': layout.leftAlign,
    'two-columns': !layout.leftAlign,
    [`hero-wrapper--${themeNames[bgColor as Color]}`]: !!bgColor,
    'hero--bottom-texture': !!bottomTexture,
    'hero-wrapper--padding': !spacing,
  })
  const heroWrapperPaddingMobile = '3.125rem'
  const heroWrapperPaddingDesktopLg = '6.25rem'
  const contentClasses = classnames({
    content: true,
    'left-align': layout.leftAlign,
  })

  const centeredContentClasses = classnames({
    centered: true,
  })

  const auxClasses = classnames({
    'background-aux-content': hasAuxContent && (media as IAuxContentLayout).isBackground,
    'foreground-aux-content': hasAuxContent && !(media as IAuxContentLayout).isBackground,
    centered: !layout.leftAlign,
  })

  const heroClassNames = classnames('hero', { 'hero-left-align-media': withLeftAlignedMedia })

  const placeAuxContentAfterHeadline = media && media && media.placeAfterHeadline

  return (
    <div className={heroWrapper}>
      <div className={heroClassNames}>
        <div className={`${contentClasses} hero-headings`}>
          {avatars && avatars.length > 0 && (
            <div className="avatars">
              {avatars.map((avatar, index) => (
                <div key={avatar.url} className={`avatar avatar--${index}`}>
                  <Image {...avatar} />
                </div>
              ))}
            </div>
          )}

          <HeroHeadings
            eyebrow={eyebrow}
            headline={headline}
            headline2={headline2}
            subheading={subheading}
            largeFont={largeFont || (!layout.compact && !layout.leftAlign)}
            placeContentAfter={placeAuxContentAfterHeadline ? 'headline' : undefined}
          >
            {placeAuxContentAfterHeadline && (
              <div className={centeredContentClasses}>
                {(media as IAuxContentLayout).renderAuxContent()}
              </div>
            )}
          </HeroHeadings>
          {(renderMainContent && renderMainContent()) || renderMainContentDefault()}
        </div>
        {hasAuxContent && !placeAuxContentAfterHeadline && (
          <div className={auxClasses}>{(media as IAuxContentLayout).renderAuxContent()}</div>
        )}
      </div>

      <style jsx>
        {`
          .hero-wrapper {
            display: ${disableGridForWrapper ? 'block' : 'grid'};
            grid-template-columns: [left-gutter] 1fr [content] 12fr [right-gutter] 1fr;
            justify-content: center;
            padding-left: 0;
            padding-right: 0;
            color: var(--text-color);
            background-color: var(--background-color);

            &--padding {
              padding-top: ${layout.compact ? '0' : heroWrapperPaddingMobile};
              padding-bottom: ${layout.compact ? '0' : heroWrapperPaddingMobile};
            }
          }

          .hero {
            grid-column: content;
            position: relative;
            display: ${disableGridForBody ? 'block' : 'grid'};
            grid-template-columns: [left-content] 7fr [right-content-sm] 1fr [right-content-lg] 5fr;
            grid-column-gap: 1.5rem;
            align-content: center;
            justify-items: center;
          }

          .hero-left-align-media {
            grid-template-columns: [right-content-sm] 5fr [right-content-lg] 1fr [middle] 1fr [left-content] 6fr;
            grid-column-gap: 0;
          }

          .one-column .hero {
            max-width: ${containerMaxWidth};
            margin: 0 auto;
          }

          .content {
            grid-column: left-content / span 3;
            order: ${withLeftAlignedMedia && 2};
            display: grid;
            text-align: center;
            justify-items: center;
            align-content: center;
            z-index: 1;
            position: relative;
          }

          :global(.hero .centered + *) {
            margin-top: 1.25rem;
          }

          .foreground-aux-content {
            grid-column: left-content / span 3;
            order: ${withLeftAlignedMedia && 1};
            border-radius: 1.5625rem;
            overflow: hidden;
          }

          .background-aux-content {
            display: none;
          }

          @media (${mq.tablet}) {
            .hero-wrapper.two-columns {
              grid-template-columns: [content] 14fr;
            }

            .two-columns .hero {
              grid-template-columns: 1fr [left-content] 7fr [right-content-sm] 1fr [right-content-lg] 5fr 1fr;
            }

            .left-align {
              justify-items: left;
              justify-self: left;
              text-align: unset;
            }

            .content {
              grid-column: left-content;
              grid-row: 1;
            }

            .two-columns .content {
              grid-column: left-content / span 3;
            }

            .foreground-aux-content {
              grid-column: right-content-sm / span 2;
              &.centered {
                grid-column: left-content / span 3;
              }
            }

            .hero .background-aux-content {
              grid-row: 1;
              grid-column: 1/-1;
              width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              overflow: hidden;
              z-index: 0;
            }

            .two-columns .background-aux-content {
              max-width: 100rem;
            }

            .centered {
              max-width: ${mqBreakpoints.laptop}px;
            }

            :global(.hero .centered + *) {
              margin-top: 3rem;
            }

            :global(.hero-headings .centered + .subheading) {
              max-width: fit-content;
            }

            :global(.centered .poster-img) {
              -webkit-mask-image: -webkit-radial-gradient(white, black);
              border-radius: 0.5rem;
            }
          }

          @media (${mq.desktopLg}) {
            .hero-wrapper {
              padding-left: 0;
              padding-right: 0;

              &--padding {
                padding-top: ${layout.compact
                  ? '0'
                  : layout.leftAlign
                  ? heroWrapperPaddingMobile
                  : heroWrapperPaddingDesktopLg};
                padding-bottom: ${layout.compact
                  ? '0'
                  : layout.leftAlign
                  ? heroWrapperPaddingMobile
                  : heroWrapperPaddingDesktopLg};
              }
            }
          }
        `}
      </style>
      <style jsx>{styles}</style>
    </div>
  )
}
